import { CaptureResult, PostHogConfig, Properties } from "posthog-js";
import { church } from "src/core/church/church";

/** Addresses whose query string can hold a typed search, such as the clearance board's ?person= */
const URL_PROPERTIES = ["$current_url", "$referrer", "$initial_current_url", "$initial_referrer"];

const stripQueryStrings = (properties: Properties | undefined) => {
    for (const key of URL_PROPERTIES) {
        const value = properties?.[key];

        if (properties && typeof value === "string") {
            properties[key] = value.split(/[?#]/)[0];
        }
    }
};

/**
 * Says whose hub an event came from, and drops the page title, which names whoever a screen is
 * about, and every address's query string. The church is added here rather than registered,
 * because signing out resets PostHog and would forget it.
 */
const addChurchAndStripIdentifyingProperties = (event: CaptureResult | null) => {
    if (!event) {
        return event;
    }

    event.properties.church_id = church.id;
    event.properties.church_slug = church.slug;

    delete event.properties.title;

    stripQueryStrings(event.properties);
    stripQueryStrings(event.$set);
    stripQueryStrings(event.$set_once);

    return event;
};

/**
 * Only events the code captures by name are sent, because headings, button labels and searches
 * carry the names of children and volunteers, and a phone calendar link carries its feed's token.
 */
export const postHogOptions: Partial<PostHogConfig> = {
    api_host: import.meta.env.VITE_PUBLIC_POSTHOG_HOST,
    capture_exceptions: true, // This enables capturing exceptions using Error Tracking, set to false if you don't want this
    debug: import.meta.env.MODE === "development",
    autocapture: false,
    capture_pageview: "history_change",
    // Rage clicks, dead clicks and heatmaps describe elements even with autocapture off
    mask_all_text: true,
    mask_all_element_attributes: true,
    session_recording: { maskAllInputs: true, maskTextSelector: "*" },
    before_send: addChurchAndStripIdentifyingProperties,
};
